import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CreditCard, Crown, Calendar, Mail, Loader2, Sparkles, Users } from "lucide-react";
import { purchasesEnabled, NATIVE_BILLING_NOTICE } from "@/lib/billing/purchaseGate";

import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useBillingMode } from "@/hooks/useBillingMode";
import type { CompanyAccount } from "@/hooks/useCompanyAccount";

interface SubscriptionInfo {
  plan: string;
  status: string;
  billing_cycle: string;
  monthly_price: number;
  seats_purchased: number;
  seats_used: number;
  trial_start_date: string | null;
  trial_end_date: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  stripe_customer_id: string | null;
  cancel_at_period_end: boolean;
}

interface Props {
  company: CompanyAccount;
  subscription: SubscriptionInfo | null;
  hideSeats?: boolean;
}

function formatPlanPrice(value: number | null | undefined, billingCycle?: string | null): string {
  if (value == null) return "—";
  const cycle = (billingCycle ?? "").toLowerCase();
  const suffix = cycle.includes("year") || cycle.includes("annual") ? "/yr" : "/mo";
  return `$${Number(value).toFixed(2)}${suffix}`;
}

export function CompanyBillingTab({ company, subscription, hideSeats = false }: Props) {
  const { isFree } = useBillingMode();
  const monthlyPrice = subscription?.monthly_price;
  const isTrialing = subscription?.status === "trialing";
  const trialDaysLeft = subscription?.trial_end_date
    ? Math.max(0, Math.ceil((new Date(subscription.trial_end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  const hasStripeCustomer = !!subscription?.stripe_customer_id;
  const [portalLoading, setPortalLoading] = useState(false);


  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-billing-portal-session');
      if (error) throw error;
      if (data?.error === 'no_customer') {
        toast.error("No billing account linked yet. Please contact support.");
        return;
      }
      if (!data?.url) {
        toast.error("Unable to open billing portal. Please contact support.");
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      console.error('billing portal error', e);
      toast.error("Unable to open billing portal. Please contact support.");
    } finally {
      setPortalLoading(false);
    }
  };

  const statusBadge = () => {
    if (!subscription) return <Badge variant="secondary">No Subscription</Badge>;
    
    if (isTrialing) return <Badge className="bg-warning-subtle text-warning-foreground">Trial — {trialDaysLeft} days left</Badge>;
    if (subscription.status === "active" && subscription.cancel_at_period_end) {
      const cancelDate = subscription.current_period_end
        ? new Date(subscription.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        : 'end of period';
      return <Badge className="bg-warning-subtle text-warning-foreground">Active — Cancels on {cancelDate}</Badge>;
    }
    if (subscription.status === "active") return <Badge className="bg-success text-success-foreground">Active</Badge>;
    if (subscription.status === "past_due") return <Badge variant="destructive">Past Due</Badge>;
    return <Badge variant="secondary">{subscription.status}</Badge>;
  };

  return (
    <div className="space-y-6">
      {isFree && purchasesEnabled() && (
        <Card className="p-4 sm:p-6 border-[hsl(var(--brand-teal))]/30 bg-[hsl(var(--brand-teal))]/5">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(var(--brand-teal))]/15 flex-shrink-0">
              <Sparkles className="h-5 w-5 text-[hsl(var(--brand-teal))]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-foreground">Free plan</h3>
                <Badge variant="secondary" className="text-[10px]">No card on file</Badge>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                You're on the Free tier. Upgrade to Premium for ad-free use, monthly credits,
                and priority support.
              </p>
              <Button asChild className="bg-[hsl(var(--brand-teal))] hover:bg-[hsl(var(--brand-teal))]/90 text-white">
                <a href="/pricing">Upgrade to Premium</a>
              </Button>
            </div>
          </div>
        </Card>
      )}
      <Card className="p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <CreditCard className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground">Subscription & Billing</h3>
            <p className="text-sm text-muted-foreground">Company plan and billing details</p>
          </div>
          {statusBadge()}
        </div>

        {subscription?.cancel_at_period_end && (
          <div className="bg-warning-subtle/30 border border-warning/20 rounded-lg p-3 text-sm text-muted-foreground">
            {purchasesEnabled() ? (
              <>
                You can reactivate anytime before your access ends by visiting{' '}
                <button onClick={openBillingPortal} className="text-primary underline hover:text-primary/80">
                  Stripe Billing Portal
                </button>.
              </>
            ) : (
              <>Your access continues until the end of the current billing period.</>
            )}
          </div>
        )}

        {subscription ? (
          <div className="space-y-6">
            {/* Plan details */}
            <div className="grid grid-cols-3 gap-4 pb-6 border-b border-border">
              <div>
                <p className="text-xs text-muted-foreground">Plan</p>
                <p className="font-semibold text-sm text-foreground flex items-center gap-1.5">
                  <Crown className="h-3.5 w-3.5 text-primary" />
                  Premium — {company.company_type.charAt(0).toUpperCase() + company.company_type.slice(1)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Price</p>
                <p className="font-semibold text-sm text-foreground">
                  {formatPlanPrice(monthlyPrice, subscription.billing_cycle)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Billing Cycle</p>
                <p className="font-semibold text-sm text-foreground capitalize">{subscription.billing_cycle || "Monthly"}</p>
              </div>
            </div>

            {/* Seats — hidden on Billing & Account page because seats are not in use */}
            {!hideSeats && (
              <div className="pb-6 border-b border-border">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-medium text-foreground">Seats</p>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-foreground">{subscription.seats_purchased || 1}</p>
                    <p className="text-xs text-muted-foreground">Purchased</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-primary">{subscription.seats_used || 1}</p>
                    <p className="text-xs text-muted-foreground">Used</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-foreground">
                      {Math.max(0, (subscription.seats_purchased || 1) - (subscription.seats_used || 1))}
                    </p>
                    <p className="text-xs text-muted-foreground">Available</p>
                  </div>
                </div>
              </div>
            )}

            {/* Trial / Period info */}
            {isTrialing && (
              <div className="bg-warning-subtle/30 border border-warning/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="h-4 w-4 text-warning" />
                  <p className="text-sm font-medium text-foreground">Free Trial</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  Your trial ends on {new Date(subscription.trial_end_date!).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.
                  After the trial, you'll be charged {formatPlanPrice(monthlyPrice, subscription.billing_cycle)}.
                </p>
              </div>
            )}

            {subscription.current_period_end && !isTrialing && (
              <div className="text-sm text-muted-foreground">
                Current billing period ends: {new Date(subscription.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            )}

            {/* Manage button — only show if Stripe customer exists */}

            {!purchasesEnabled() ? (
              <div className="bg-muted/50 rounded-lg p-4 text-center">
                <p className="text-sm text-muted-foreground">{NATIVE_BILLING_NOTICE}</p>
              </div>
            ) : hasStripeCustomer ? (
              <Button
                variant="outline"
                onClick={openBillingPortal}
                disabled={portalLoading}
                className="w-full"
              >
                {portalLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="h-4 w-4 mr-2" />
                )}
                {portalLoading ? "Opening…" : "Manage Subscription"}
              </Button>
            ) : (
              <div className="bg-muted/50 rounded-lg p-4 text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  Billing management will be available once your subscription is linked to a payment method.
                </p>
                <Button variant="outline" size="sm" asChild>
                  <a href="mailto:support@lanerunner.com">
                    <Mail className="h-4 w-4 mr-2" />
                    Contact Support
                  </a>
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-sm text-muted-foreground">No active subscription found for this company.</p>
          </div>
        )}
      </Card>
    </div>
  );
}
